import axios from 'axios'
import Request from './request'
import MessengerRequest from './outbox_request'
import { AxiosResponseType } from 'website/BulkMoneySender/types'
import { CreateDraftResponse } from 'types/api/responses/sendsms'
import { Pagination } from 'types/api/responses/senderIds'
import type { ContactFilterPayload } from 'website/Messaging/audience'

const CLOUD_API_VERSION = 'v20.0'

// type WabaButton = {
// 	text: 'string'
// 	type: 'URL' | 'PHONE_NUMBER'
// 	url?: string
// 	phone_number?: string
// }

// type Component = {
// 	format?: 'TEXT' | 'DOCUMENT'
// 	text?: string
// 	buttons?: WabaButton[]
// 	type: 'HEADER' | 'BODY' | 'BUTTONS' | 'FOOTER'
// 	example?: {
// 		body_text?: string[][]
// 		header_handle?: string[]
// 	}
// }

export type HeaderFormats = 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION'

type Header = {
	example?: {
		body_text?: string[][]
		header_handle?: string[]
	}
	format: HeaderFormats
	text?: string
}

export type WabaButton = {
	text: string
	type: 'URL' | 'PHONE_NUMBER' | 'QUICK_REPLY' | 'COPY_CODE' | 'OTP' | 'MPM' | 'CATALOG' | 'FLOW' | 'SPM' | string
	url?: string
	phone_number?: string
}

type WabaTemplateComponent = {
	buttons?: WabaButton[]
	cards?: { components?: WabaTemplateComponent[] }[]
	example?: Header['example']
	format?: HeaderFormats | 'PRODUCT' | string
	text?: string
	type: 'HEADER' | 'BODY' | 'BUTTONS' | 'FOOTER' | 'CAROUSEL' | 'LIMITED_TIME_OFFER' | string
}

export type WabaMessageTemplate = {
	account_id: number
	components?: WabaTemplateComponent[] | null
	id: string
	language: string
	message: string
	name: string
	shared_review_id?: number
	shared_review_message?: string
	shared_review_status?: 'approved' | 'pending' | 'rejected'
	status: 'APPROVED' | 'REJECTED' | 'PENDING'
	type: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION'
	header?: Header
	footer?: string
	button?: WabaButton[] | null
	// category: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION'
	// components: Component[]
	// id: string
	// language: string
	// library_template_name: string
	// name: string
	// status: 'APPROVED' | 'REJECTED' | 'PENDING'
}

export type OrganizationTemplateStatus = 'approved' | 'pending' | 'rejected'
export type OrganizationTemplateReviewDecision = 'approve' | 'manual_review' | 'reject'
export type OrganizationTemplateReviewRiskLevel = 'low' | 'medium' | 'high' | 'unknown'

export type OrganizationTemplate = {
	id: number
	organization_id: number
	creator_id: number
	creator_name: string
	creator_email?: string | null
	template?: WabaMessageTemplate | null
	template_id: string
	template_name: string
	status: OrganizationTemplateStatus
	created_at: string
	updated_at: string
}

export type OrganizationTemplateList = {
	organization_templates: OrganizationTemplate[]
}

export type OrganizationTemplateReviewRecommendation = {
	organization_template_id: number
	template?: WabaMessageTemplate | null
	template_id: string
	template_name: string
	decision: OrganizationTemplateReviewDecision
	risk_level: OrganizationTemplateReviewRiskLevel
	summary: string
	reasons: string[]
	recommended_action: string
	source: string
}

type Paging = {
	cursors: {
		before: string
		after: string
	}
}

type WabaTemplateData = {
	data: { data: WabaMessageTemplate[]; paging: Paging }
}

// Supplied at build time via the VITE_WA_ACCESS_TOKEN environment variable.
const accessToken = import.meta.env.VITE_WA_ACCESS_TOKEN ?? ''

export function getWabaMessageTemplates({ wabaId }: { wabaId: number }): Promise<WabaTemplateData> {
	return axios.get(`https://graph.facebook.com/${CLOUD_API_VERSION}/${wabaId}/message_templates`, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	})
}

type Parameter = {
	type: 'text' | 'location' | 'action'
	text?: string
	action?: any
	location?: {
		latitude: string
		longitude: string
		name: string
		address: string
	}
}

export type RequestComponent = {
	type: 'body' | 'header' | 'footer' | 'buttons' | 'button'
	sub_type?: 'flow'
	index?: string
	parameters: Parameter[]
}

export function sendWabaMessage({
	phoneNumberId,
	templateName,
	templateLanguageCode,
	recipientPhoneNumber,
	components,
}: {
	phoneNumberId: string
	templateName: string
	templateLanguageCode: string
	recipientPhoneNumber: string
	components?: RequestComponent[]
}) {
	const data = {
		messaging_product: 'whatsapp',
		recipient_type: 'individual',
		to: recipientPhoneNumber,
		type: 'template',
		template: {
			name: templateName,
			language: {
				code: templateLanguageCode,
			},
			...(components && { components }),
		},
	}

	return axios.post(`https://graph.facebook.com/${CLOUD_API_VERSION}/${phoneNumberId}/messages`, data, {
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	})
}

// {
// 	accept_terms: boolean
// 	account_id: number
// 	phone_number_id_id: number
// 	phone_numbers?: string[]
// 	file?: string
// 	file_extension?: string
// 	template_id: string
// }

type DraftPayload = {
	accept_terms: boolean
	account_id: number
	phone_number_id_id: number
	phone_numbers?: string[]
	template_id: string
	all_contacts: boolean
	group_ids?: number[]
	contact_ids?: number[]
	contact_filter?: ContactFilterPayload
	variable_mappings?: Record<string, string>
}

export function createWADraft(data: DraftPayload | FormData, headers?: any): Promise<AxiosResponseType<CreateDraftResponse>> {
	return Request.post({
		url: '/drafts',
		data,
		...(headers && { headers }),
	})
}

export function createWAOutboxRetryDraft(correlatorIDs: string[]): Promise<AxiosResponseType<CreateDraftResponse>> {
	return Request.post({
		url: '/drafts/retry',
		data: { correlator_ids: correlatorIDs },
	})
}

type RecipientFileHeadersResponse = {
	headers: string[]
}

export function previewWADraftRecipientFileHeaders(data: FormData): Promise<AxiosResponseType<RecipientFileHeadersResponse>> {
	return Request.post({
		url: '/drafts/recipient_file_headers',
		data,
		headers: { 'Content-type': 'multipart/form-data' },
	})
}

export function createWADraftAsset(
	draftId: number,
	data: DraftPayload | FormData | null,
): Promise<AxiosResponseType<CreateDraftResponse>> {
	return Request.put({
		url: `/drafts/${draftId}/media_assets`,
		data: data ?? undefined,
		headers: { 'Content-type': 'multipart/form-data' },
	})
}

export type WADraftRecipient = {
	created_at: string
	custom_fields: any
	draft_id: number
	id: number
	message: string
	number: string
	sheet_row: number
	updated_at: string
}

export type PhoneNumberId = {
	business_account_id: number
	created_at: string
	id: number
	meta_business_id?: string
	phone_number: string
	phone_number_id: string
	registration_mode: 'normal' | 'coexistence'
	updated_at: string
	verified_name: string
	messaging_limit: number
}

export type WhatsAppReadinessAction = {
	label: string
	type: 'external_link' | 'support' | 'relink' | 'refresh' | 'route'
	url?: string
}

export type WhatsAppReadinessCheck = {
	status: 'passed' | 'warning' | 'failed' | 'unknown'
	label: string
}

export type WhatsAppReadiness = {
	status: 'ready' | 'limited' | 'action_needed' | 'blocked'
	status_label: string
	reason_code: string
	reason_message: string
	meta_error_code?: string
	meta_error_subcode?: string
	meta_error_message?: string
	can_send: boolean
	requires_user_action: boolean
	primary_action?: WhatsAppReadinessAction
	secondary_actions?: WhatsAppReadinessAction[]
	checks: {
		verification: WhatsAppReadinessCheck
		connection: WhatsAppReadinessCheck
		token: WhatsAppReadinessCheck
		templates: WhatsAppReadinessCheck
		payment_method: WhatsAppReadinessCheck
		messaging_capacity: WhatsAppReadinessCheck
		quality_rating: WhatsAppReadinessCheck
	}
	capabilities: {
		phone_number_id: string
		display_phone_number: string
		is_shared_number: boolean
		is_on_business_app: boolean
		contact_tag_field_key?: string
		contact_tag_field_value?: string
		meta_business_id?: string
		messaging_limit_tier: number
		messaging_limit_label: string
		platform_type?: string
		registration_mode: 'normal' | 'coexistence'
		throughput_level?: string
		quality_rating?: string
		unified_billing: boolean
	}
	activation_guide?: {
		test_recipient: string
		suggested_template_name: string
		suggested_template_body: string
		create_template_path: string
		send_test_message_path: string
	}
	checked_at: string
}

export function listAccountWAPhoneNumbers(
	accountId: number,
): Promise<AxiosResponseType<{ pagination: Pagination; phone_number_ids: PhoneNumberId[] }>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids`,
	})
}

export function unlinkAccountWAPhoneNumber(accountId: number, phoneNumberIdId: number): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}`,
	})
}

export function updateAccountWAPhoneNumberAccessToken(
	accountId: number,
	phoneNumberIdId: number,
	accessToken: string,
): Promise<AxiosResponseType<{ success: boolean }>> {
	return Request.patch({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/access_token`,
		data: { access_token: accessToken },
	})
}

export function getAccountWAPhoneNumberReadiness(
	accountId: number,
	phoneNumberIdId: number,
): Promise<AxiosResponseType<WhatsAppReadiness>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/readiness`,
	})
}

export type CoexistenceContactSyncPhase = 'pending' | 'importing' | 'error' | 'not_tracked'

export type CoexistenceContactSyncStatus = {
	phase: CoexistenceContactSyncPhase
	webhook_batches: number
	contacts_created: number
	contacts_updated: number
	contacts_unchanged: number
	contacts_skipped: number
	contacts_applied: number
	last_error?: string
	last_activity_at?: string
	meta_phone_number_id: string
	contact_sync_request_allowed?: boolean
	contact_sync_request_deadline?: string
	contact_sync_request_blocked_reason?: string
	contact_sync_request_window_started_at?: string
	contact_sync_request_window_duration_ms?: number
}

export function getCoexistenceContactSyncStatus(
	accountId: number,
	phoneNumberIdId: number,
): Promise<AxiosResponseType<CoexistenceContactSyncStatus>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/coexistence_contact_sync`,
	})
}

export type RequestCoexistenceContactSyncResponse = {
	ok: boolean
	message: string
}

/** Ask Meta to send contact sync webhooks (coexistence SMB app data). */
export function requestCoexistenceContactSync(
	accountId: number,
	phoneNumberIdId: number,
): Promise<AxiosResponseType<RequestCoexistenceContactSyncResponse>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/coexistence_contact_sync`,
	})
}

export function listAccountWATemplates(
	accountId: number,
	phoneNumberId_id: number,
): Promise<AxiosResponseType<WabaMessageTemplate[]>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberId_id}/templates`,
	})
}

export type WhatsAppFlowStatus = 'DRAFT' | 'PUBLISHED' | 'DEPRECATED' | 'THROTTLED' | string

export type WhatsAppFlowValidationError = {
	error?: string
	error_type?: string
	message?: string
	line_start?: number
	line_end?: number
	column_start?: number
	column_end?: number
	pointers?: string[]
}

export type WhatsAppFlow = {
	id: string
	name: string
	status: WhatsAppFlowStatus
	categories?: string[]
	validation_errors?: WhatsAppFlowValidationError[]
	preview?: {
		preview_url?: string
		expires_at?: string
	}
	json_version?: string
	data_api_version?: string
	data_channel_uri?: string
	endpoint_uri?: string
}

export type CreateWhatsAppFlowPayload = {
	name: string
	categories: string[]
	clone_flow_id?: string
	endpoint_uri?: string
}

export function listWhatsAppFlows(
	accountId: number,
	phoneNumberIdId: string | number,
): Promise<AxiosResponseType<{ flows: WhatsAppFlow[] }>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows`,
	})
}

export function createWhatsAppFlow(
	accountId: number,
	phoneNumberIdId: string | number,
	data: CreateWhatsAppFlowPayload,
): Promise<AxiosResponseType<WhatsAppFlow>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows`,
		data,
	})
}

export function getWhatsAppFlow(
	accountId: number,
	phoneNumberIdId: string | number,
	flowId: string,
): Promise<AxiosResponseType<WhatsAppFlow>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows/${flowId}`,
	})
}

export function getWhatsAppFlowPreview(
	accountId: number,
	phoneNumberIdId: string | number,
	flowId: string,
	invalidate = false,
): Promise<AxiosResponseType<WhatsAppFlow>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows/${flowId}/preview`,
		params: { invalidate },
	})
}

export function publishWhatsAppFlow(
	accountId: number,
	phoneNumberIdId: string | number,
	flowId: string,
): Promise<AxiosResponseType<{ success: boolean }>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows/${flowId}/publish`,
	})
}

export function updateWhatsAppFlowJSON(
	accountId: number,
	phoneNumberIdId: string | number,
	flowId: string,
	flowJson: string,
): Promise<AxiosResponseType<{ success: boolean; validation_errors?: WhatsAppFlowValidationError[] }>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/flows/${flowId}/assets`,
		data: { flow_json: flowJson },
	})
}

export function listWADraftRecipients(
	draftId: string | number,
	params?: any,
): Promise<AxiosResponseType<{ draft_recipients: WADraftRecipient[] }>> {
	return Request.get({
		url: `/drafts/${draftId}/draft_recipients`,
		params,
	})
}

export function sendWADraft(draftId: string | number) {
	return Request.post({
		url: `/drafts/${draftId}`,
	})
}

export type WhatsAppSchedulePayload = {
	send_at?: string | null
	repeated_dates?: string[]
}

export function scheduleWADraft(
	draftId: string | number,
	data: WhatsAppSchedulePayload,
): Promise<AxiosResponseType<CreateDraftResponse>> {
	return Request.post({
		url: `/drafts/${draftId}/schedule`,
		data,
	})
}

export type SMSWhatsAppFallbackPlanPayload = {
	audience_id?: number
	budget_amount?: number
	message_id?: number
	phone_number_id_id: number
	template_id: string
	template_language?: string
	template_message?: string
	template_name?: string
	variable_mappings?: Record<string, string>
	wait_seconds: number
}

export type SMSWhatsAppFallbackPlan = {
	id: number
	account_id: number
	user_id: number
	source_type: 'sms_audience' | 'sms_message'
	source_id: number
	phone_number_id: string
	phone_number_id_id: number
	template_id: string
	template_language: string
	template_name: string
	message: string
	wait_seconds: number
	release_at: string
	status: 'scheduled' | 'processing' | 'completed' | 'failed' | 'cancelled'
	currency: string
	budget_amount?: number
	recipient_count: number
	attempted_count: number
	skipped_count: number
	total_cost: number
	created_at: string
	updated_at: string
}

export type SMSWhatsAppFallbackPlanList = {
	sms_whatsapp_fallback_plans: SMSWhatsAppFallbackPlan[]
	pagination: Pagination
}

export function createSMSWhatsAppFallbackPlan(
	accountId: number,
	data: SMSWhatsAppFallbackPlanPayload,
): Promise<AxiosResponseType<SMSWhatsAppFallbackPlan>> {
	return Request.post({
		url: `/accounts/${accountId}/sms_whatsapp_fallbacks`,
		data,
	})
}

export function listSMSWhatsAppFallbackPlans(
	accountId: number,
	params: any,
): Promise<AxiosResponseType<SMSWhatsAppFallbackPlanList>> {
	return Request.get({
		url: `/accounts/${accountId}/sms_whatsapp_fallbacks`,
		params,
	})
}

export function deleteSMSWhatsAppFallbackPlans(ids: number[]): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: `/sms_whatsapp_fallbacks?ids=${ids.join()}`,
	})
}

export type OutboxMessage = {
	id: number
	account_id: number
	message: string
	phone_number_id: string
	sender_phone_number?: string
	process_time: string
	recipient_count: number
	send_at?: string
	source?: 'user_scheduled' | 'capacity_batch' | 'sms_whatsapp_fallback'
	status?: string
	template_id: string
	template_name: string
	total_cost: number
	type: 'template'
	user_id: number
	created_at: string
	updated_at: string
	currency?: string
	name?: string
	scheduled_item_type?: 'whatsapp_scheduled' | 'sms_whatsapp_fallback'
	wait_seconds?: number
	budget_amount?: number
}

export function listWAOutboxMessages(accountId: number, params: any): Promise<AxiosResponseType<any>> {
	return MessengerRequest.get({
		url: `/accounts/${accountId}/messages`,
		params,
	})
}

export function listScheduledWAMessages(accountId: number, params: any): Promise<AxiosResponseType<any>> {
	return Request.get({
		url: `/accounts/${accountId}/scheduled_whatsapp_messages`,
		params,
	})
}

export function deleteScheduledWAMessages(ids: number[]): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: `/scheduled_whatsapp_messages?ids=${ids.join()}`,
	})
}

function generateAnchorTagAndDownload(response: { data: any }, filename: string) {
	const url = window.URL.createObjectURL(new Blob([response.data]))
	const link = document.createElement('a')
	link.href = url
	link.setAttribute('download', filename)
	if (document.body) {
		document.body.appendChild(link)
		link.click()
	}
}

export async function exportVariablesFile({
	filename,
	PhoneNumberIdId,
	templateId,
}: {
	filename: string
	PhoneNumberIdId: number
	templateId: string
}): Promise<any> {
	const response = await Request.filedownload({
		url: `/phone_number_id_id/${PhoneNumberIdId}/templates/${templateId}/export_variables`,
	})
	generateAnchorTagAndDownload(response, filename)
}

export function getSummary(draftId: string | number): Promise<AxiosResponseType<any>> {
	return Request.get({
		url: `/drafts/${draftId}/draft_summary`,
	})
}

export function getDraftById(draftId: string | number): Promise<AxiosResponseType<any>> {
	return Request.get({
		url: `/drafts/${draftId}`,
	})
}

export function deleteDraft(draftId: string | number): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: `/drafts/${draftId}`,
	})
}

export function discardWAInvalidRecipients(draftId: string | number): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: `/drafts/${draftId}/invalid_recipients`,
	})
}

export function deleteDraftRecipients(recipient_ids: string): Promise<AxiosResponseType<any>> {
	return Request.delete({
		url: '/draft_recipients',
		params: {
			recipient_ids,
		},
	})
}

export function editDraftRecipient(recipient_id: number, data: { number: string }) {
	return Request.put({
		url: `/draft_recipients/${recipient_id}`,
		data,
	})
}

export function fetchMessageRecipients(messageId: string | number, params: any): Promise<AxiosResponseType<any>> {
	return MessengerRequest.get({
		url: `/messages/${messageId}/recipients`,
		params,
	})
}

export function exportWARecipients({
	messageId,
	filename,
	params,
}: {
	messageId: string | number
	filename: string
	params?: any
}): Promise<void> {
	return MessengerRequest.filedownload({
		url: `/messages/${messageId}/export_recipients`,
		params,
	}).then(response => {
		generateAnchorTagAndDownload(response, filename)
	})
}

export function fetchOutboxSummary(messageId: number, params?: any): Promise<AxiosResponseType<any>> {
	return MessengerRequest.get({
		url: `/messages/${messageId}/delivery_report_summary`,
		params,
	})
}

export function fetchSummaryDetails(messageId: string | number): Promise<AxiosResponseType<any>> {
	return MessengerRequest.get({
		url: `/messages/${messageId}`,
	})
}

type WAConfigurationPayload = {
	access_token: string
	account_id: number
	business_account_id: string
	phone_number: string
	phone_number_id: string
}

export function setupWAConfigurations(data: WAConfigurationPayload) {
	return Request.post({
		url: `/phone_number_ids`,
		data,
	})
}

export function sendEmbeddedSignupDataToServer({
	code,
	phoneNumberId,
	wabaId,
	businessId,
	type,
}: {
	code: string
	phoneNumberId?: string
	wabaId?: string
	businessId?: string
	type: 'normal' | 'coexistence'
}): Promise<any> {
	return Request.post({
		url: `/embedded_signup`,
		data: {
			code,
			phone_number_id: phoneNumberId,
			waba_id: wabaId,
			business_id: businessId,
			account_id: parseInt(window.localStorage.getItem('x-smsleopard-account-id') ?? '0'),
			type,
		},
	})
}

export function getWabaCatalogProducts(phoneNumberId: string) {
	return Request.get({
		url: `/phone_number_ids/${phoneNumberId}/catalogue_products`,
	})
}

export type WhatsAppProductCollectionProductItem = {
	product_retailer_id: string
}

export type WhatsAppProductCollection = {
	account_id: number
	catalog_id: string
	created_at?: string
	id: number
	name: string
	phone_number_id_id: number
	product_item_count: number
	product_items: WhatsAppProductCollectionProductItem[]
	updated_at?: string
}

export type WhatsAppProductCollectionPayload = {
	catalog_id: string
	name: string
	product_items: WhatsAppProductCollectionProductItem[]
}

export function listWabaProductCollections(
	accountId: number,
	phoneNumberIdId: number,
): Promise<AxiosResponseType<{ product_collections: WhatsAppProductCollection[] }>> {
	return Request.get({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/product_collections`,
	})
}

export function createWabaProductCollection(
	accountId: number,
	phoneNumberIdId: number,
	payload: WhatsAppProductCollectionPayload,
): Promise<AxiosResponseType<WhatsAppProductCollection>> {
	return Request.post({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/product_collections`,
		data: payload,
	})
}

export function updateWabaProductCollection(
	accountId: number,
	phoneNumberIdId: number,
	collectionId: number,
	payload: WhatsAppProductCollectionPayload,
): Promise<AxiosResponseType<WhatsAppProductCollection>> {
	return Request.patch({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/product_collections/${collectionId}`,
		data: payload,
	})
}

export function deleteWabaProductCollection(
	accountId: number,
	phoneNumberIdId: number,
	collectionId: number,
): Promise<AxiosResponseType<{ success: boolean }>> {
	return Request.delete({
		url: `/accounts/${accountId}/phone_number_ids/${phoneNumberIdId}/product_collections/${collectionId}`,
	})
}

export type CreatedWATemplate = {
	category?: string
	id?: string
	language?: string
	name: string
	status?: string
}

export const createWATemplate = (data: FormData): Promise<AxiosResponseType<CreatedWATemplate>> =>
	Request.post({ url: '/templates', data, headers: { 'Content-type': undefined } })

export const deleteWATemplate = (phoneNumberIdId: number, templateId: string) =>
	Request.delete({ url: `/phone_number_id_id/${phoneNumberIdId}/template_id/${templateId}` })

export function listOrganizationTemplates(
	status?: OrganizationTemplateStatus | 'all',
): Promise<AxiosResponseType<OrganizationTemplateList>> {
	return Request.get({
		url: '/organization_templates',
		params: status && status !== 'all' ? { status } : {},
	})
}

export function processOrganizationTemplateReview(
	organizationTemplateId: number,
	status: Exclude<OrganizationTemplateStatus, 'pending'>,
): Promise<AxiosResponseType<OrganizationTemplate>> {
	return Request.put({
		url: '/organization_templates',
		data: {
			organization_template_id: organizationTemplateId,
			status,
		},
	})
}

export function getOrganizationTemplateReviewRecommendation(
	organizationTemplateId: number,
): Promise<AxiosResponseType<OrganizationTemplateReviewRecommendation>> {
	return Request.get({
		url: `/organization_templates/${organizationTemplateId}/recommendation`,
	})
}
